import React, { useState, useRef, useEffect } from "react";

const GEOAPIFY_KEY = process.env.REACT_APP_GEOAPIFY_API_KEY || "";

export default function GeoSearch({
    value,
    onChange,
    onSelect,
    country = "",
    placeholder = "Search address...",
    className = "form-control",
    minLength = 2,
    debounceMs = 350,
    limit = 6
}) {
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [focused, setFocused] = useState(false);

    const timerRef = useRef(null);
    const abortRef = useRef(null);
    const mouseDownOnSuggestion = useRef(false);
    const inputRef = useRef(null);

    useEffect(() => {
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
            if (abortRef.current) abortRef.current.abort();
        };
    }, []);

    function fetchSuggestions(query) {
        if (!GEOAPIFY_KEY || !query || query.length < minLength) {
            setSuggestions([]);
            return;
        }

        if (abortRef.current) {
            abortRef.current.abort();
            abortRef.current = null;
        }

        const controller = new AbortController();
        abortRef.current = controller;

        setLoading(true);
        setError(null);

        let url = `https://api.geoapify.com/v1/geocode/search?text=${encodeURIComponent(query)}&format=json&apiKey=${GEOAPIFY_KEY}&limit=${limit}`;
        if (country) {
            url += `&filter=countrycode:${encodeURIComponent(country)}`;
        }

        console.log("GeoSearch fetching:");
        fetch(url, { signal: controller.signal })
            .then(res => {
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                return res.json();
            })
            .then(data => {
                // Geoapify may return features[] or results[]
                let items = [];
                if (Array.isArray(data?.features)) {
                    items = data.features.map(f => ({
                        formatted: f.properties?.formatted || f.properties?.name || "",
                        lat: f.geometry?.coordinates?.[1],
                        lon: f.geometry?.coordinates?.[0],
                        details: f.properties
                    }));
                } else if (Array.isArray(data?.results)) {
                    items = data.results.map(r => ({
                        formatted: r.formatted || r.name || "",
                        lat: r.lat,
                        lon: r.lon,
                        details: r
                    }));
                } else if (Array.isArray(data?.results ?? data?.features ?? [])) {
                    // fallback
                    items = (data.results || data.features).map(x => ({
                        formatted: x.formatted || x.properties?.formatted || x.name || "",
                        lat: x.lat || x.geometry?.coordinates?.[1],
                        lon: x.lon || x.geometry?.coordinates?.[0],
                        details: x
                    }));
                }
                setSuggestions(items);
            })
            .catch(err => {
                if (err.name === "AbortError") return;
                console.error("GeoSearch fetch failed:", err);
                setError(err instanceof Error ? err.message : String(err));
                setSuggestions([]);
            })
            .finally(() => {
                setLoading(false);
                abortRef.current = null;
            });
    }

    function handleInputChange(e) {
        const v = e.target.value;
        onChange?.(v);

        setSuggestions([]);
        setError(null);

        if (timerRef.current) clearTimeout(timerRef.current);

        if (!v || v.length < minLength) {
            setSuggestions([]);
            return;
        }

        timerRef.current = setTimeout(() => fetchSuggestions(v), debounceMs);
    }

    function handleSelect(item) {
        if (!item) return;
        onSelect?.(item);
        setSuggestions([]);
        setFocused(false);

        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        if (abortRef.current) {
            abortRef.current.abort();
            abortRef.current = null;
        }

        mouseDownOnSuggestion.current = false;
    }

    function handleBlur() {
        // delay so click on suggestion can be processed
        setTimeout(() => {
            if (mouseDownOnSuggestion.current) {
                // a suggestion press is happening; keep visible briefly
                return;
            }
            setFocused(false);
            setSuggestions([]);
        }, 150);
    }

    const show = focused && (loading || suggestions.length > 0 || !!error);

    return (
        <div style={{ position: "relative" }}>
            <input
                ref={inputRef}
                className={className}
                value={value}
                onChange={handleInputChange}
                onFocus={() => {
                    setFocused(true);
                    if (value && value.length >= minLength) {
                        if (timerRef.current) clearTimeout(timerRef.current);
                        // run immediately
                        timerRef.current = setTimeout(() => fetchSuggestions(value), 0);
                    }
                }}
                onBlur={handleBlur}
                placeholder={placeholder}
                autoComplete="off"
            />

            {show && (
                <div className="list-group position-absolute w-100" style={{ zIndex: 1000, maxHeight: 260, overflow: "auto" }}>
                    {loading && <div className="list-group-item">Searching...</div>}
                    {error && <div className="list-group-item text-danger">{String(error)}</div>}
                    {!loading && suggestions.length === 0 && !error && (
                        <div className="list-group-item text-muted">No results</div>
                    )}
                    {suggestions.map((s, i) => (
                        <button
                            key={i}
                            type="button"
                            className="list-group-item list-group-item-action"
                            onMouseDown={() => { mouseDownOnSuggestion.current = true; }}
                            onClick={() => handleSelect(s)}
                        >
                            <div>{s.formatted}</div>
                            {s.details?.city && <small className="text-muted">{s.details.city}</small>}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}